import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuthicatedUser } from "react-redux-django-auth";
import { Loader2, GraduationCap } from "lucide-react";
import { useToast } from "../../hooks/otherHooks/useToast";
import { ROLES, DASHBOARD_ROUTES, RoleType } from "../../constants/roles";

export interface UserData {
  id?: number | string;
  email?: string;
  user_type?: "STUDENT" | "INSTRUCTOR" | "ADMIN" | "SUPERADMIN" | string;
  role?: "STUDENT" | "INSTRUCTOR" | "ADMIN" | "SUPERADMIN" | string;
  roles?: string[];
  is_staff?: boolean;
  is_superuser?: boolean;
  is_email_verified?: boolean;
  first_name?: string;
  last_name?: string;
  photo_url?: string;
  get_photo_url?: string;
}

export interface AuthGuardProps {
  children?: React.ReactNode;
  allowedRoles?: RoleType[];
}

export function AuthGuard({ children, allowedRoles }: AuthGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, isLoading } = useAuthicatedUser();
  const { addToast } = useToast();

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // 🛑 Wait until auth state finishes initializing
    if (isLoading) {
      setIsReady(false);
      return;
    }

    const pathname = location.pathname || "/";
    const normalizedPathname = (pathname || "/").replace(/\/$/, "") || "/";

    const PUBLIC_ROUTES = [
      "/",
      "/about",
      "/contact",
      "/services",
      "/recent-jobs",
      "/courses",
      "/blog",
      "/privacy-policy",
      "/return-policy",
      "/terms-and-conditions",
      "/careers",
      "/verify-certificate",
      "/birthday-flyer",
      "/login",
      "/create-account",
      "/reset-password",
      "/auth/login",
      "/auth/signup",
      "/auth/reset-password",
    ];

    const isPublicRoute =
      PUBLIC_ROUTES.includes(pathname) ||
      PUBLIC_ROUTES.includes(normalizedPathname) ||
      normalizedPathname.startsWith("/blog") ||
      normalizedPathname.startsWith("/services") ||
      normalizedPathname.startsWith("/service") ||
      normalizedPathname.startsWith("/recent-jobs") ||
      normalizedPathname.startsWith("/courses") ||
      normalizedPathname.startsWith("/track") ||
      normalizedPathname.startsWith("/careers") ||
      normalizedPathname.startsWith("/activate") ||
      normalizedPathname.startsWith("/password/reset/confirm") ||
      normalizedPathname.startsWith("/auth/");

    const inAuthGroup =
      normalizedPathname === "/login" ||
      normalizedPathname === "/create-account" ||
      normalizedPathname === "/reset-password" ||
      normalizedPathname === "/auth/login" ||
      normalizedPathname === "/auth/signup" ||
      normalizedPathname === "/auth/reset-password" ||
      normalizedPathname.startsWith("/auth/");

    const inStudentGroup = normalizedPathname.startsWith("/student");
    const inInstructorGroup = normalizedPathname.startsWith("/instructor");
    const inStaffGroup = normalizedPathname.startsWith("/staff") || normalizedPathname.startsWith("/admin");
    const inSuperAdminGroup = normalizedPathname.startsWith("/superadmin");
    const inVerificationGroup = normalizedPathname.startsWith("/verification");
    const inDashboardGroup = normalizedPathname.startsWith("/dashboard");

    const isProtectedRoute =
      inStudentGroup ||
      inInstructorGroup ||
      inStaffGroup ||
      inSuperAdminGroup ||
      inVerificationGroup ||
      inDashboardGroup ||
      Boolean(allowedRoles && allowedRoles.length > 0);

    // Case 1: Unauthenticated Users
    if (isAuthenticated === false) {
      if (isProtectedRoute) {
        setIsReady(false);
        if (addToast) {
          addToast({
            message: "Please log in to access this area.",
            bg: "warning",
          });
        }
        navigate(`/login?ReturnUrl=${encodeURIComponent(pathname)}`, { replace: true });
      } else {
        setIsReady(true);
      }
      return;
    }

    // Case 2: Authenticated but waiting for user profile payload
    const userData: UserData | undefined =
      (currentUser as any)?.user ||
      (currentUser as any)?.data?.user ||
      (currentUser as any)?.data ||
      currentUser;

    if (isAuthenticated === true && !userData) {
      // If we have an explicit return destination on auth page, redirect immediately
      const searchParams = new URLSearchParams(location.search);
      const returnUrlParam = searchParams.get("ReturnUrl") || searchParams.get("returnUrl");
      const blogReturnUrl = localStorage.getItem("blogReturnUrl");
      if (inAuthGroup && (returnUrlParam || blogReturnUrl)) {
        if (blogReturnUrl) localStorage.removeItem("blogReturnUrl");
        navigate(returnUrlParam || blogReturnUrl!, { replace: true });
        return;
      }
      setIsReady(false);
      return;
    }

    // Case 3: Authenticated with user roles & destination routing
    if (isAuthenticated === true && userData) {
      const isSuperuser = Boolean(
        userData.is_superuser ||
        (currentUser as any)?.is_superuser ||
        userData.role === "SUPERADMIN" ||
        userData.role === "SuperAdmin"
      );

      // Normalize all roles into uppercase strings
      const rawRolesList: string[] = Array.isArray(userData.roles) && userData.roles.length > 0
        ? userData.roles
        : [userData.role || userData.user_type || (isSuperuser ? "SUPERADMIN" : (userData.is_staff ? "ADMIN" : "STUDENT"))];

      const userRoles: string[] = rawRolesList.map((r) => String(r).toUpperCase());
      if (isSuperuser && !userRoles.includes(ROLES.SUPERADMIN)) {
        userRoles.push(ROLES.SUPERADMIN);
      }
      if (userData.is_staff && !userRoles.includes(ROLES.ADMIN)) {
        userRoles.push(ROLES.ADMIN);
      }

      // Determine active role based on last_active_role from backend
      const rawActiveRole = (
        (userData as any).last_active_role ||
        userData.role ||
        ""
      ).toUpperCase() as RoleType;

      let primaryRole: RoleType = ROLES.STUDENT;
      if (rawActiveRole && (isSuperuser || userRoles.includes(rawActiveRole))) {
        primaryRole = rawActiveRole;
      } else if (isSuperuser || userRoles.includes(ROLES.SUPERADMIN)) {
        primaryRole = ROLES.SUPERADMIN;
      } else if (userRoles.includes(ROLES.ADMIN)) {
        primaryRole = ROLES.ADMIN;
      } else if (userRoles.includes(ROLES.INSTRUCTOR)) {
        primaryRole = ROLES.INSTRUCTOR;
      } else {
        primaryRole = ROLES.STUDENT;
      }

      // Return destination handling (e.g. ReturnUrl or blogReturnUrl)
      const searchParams = new URLSearchParams(location.search);
      const returnUrlParam =
        searchParams.get("ReturnUrl") ||
        searchParams.get("returnUrl") ||
        searchParams.get("redirect") ||
        searchParams.get("redirect_url");

      const blogReturnUrl = localStorage.getItem("blogReturnUrl");
      if (blogReturnUrl) {
        localStorage.removeItem("blogReturnUrl");
      }

      const returnDestination = returnUrlParam || blogReturnUrl;

      // 3A. Authenticated user on auth pages (login/signup) -> dispatch based on last active role
      if (inAuthGroup) {
        if (returnDestination && returnDestination !== "/login" && returnDestination !== "/") {
          navigate(returnDestination, { replace: true });
          return;
        }

        const target = DASHBOARD_ROUTES[primaryRole] || "/student/dashboard";
        navigate(target, { replace: true });
        return;
      }

      // 3B. If user accesses generic /dashboard root, send to their highest portal
      if (normalizedPathname === "/dashboard") {
        navigate(DASHBOARD_ROUTES[primaryRole] || "/student/dashboard", { replace: true });
        return;
      }

      // 3C. Explicit allowedRoles check (when component is passed allowedRoles prop)
      if (allowedRoles && allowedRoles.length > 0) {
        const hasExplicitAccess =
          isSuperuser ||
          userRoles.includes(ROLES.SUPERADMIN) ||
          userRoles.some((r) => allowedRoles.includes(r as RoleType));

        if (!hasExplicitAccess) {
          navigate(DASHBOARD_ROUTES[primaryRole] || "/student/dashboard", { replace: true });
          if (addToast) {
            addToast({
              message: "Access restricted to authorized personnel.",
              bg: "danger",
            });
          }
          return;
        }

        setIsReady(true);
        return;
      }

      // 3D. Automatic Route Group Enforcement
      if (inSuperAdminGroup && !isSuperuser && !userRoles.includes(ROLES.SUPERADMIN)) {
        navigate(DASHBOARD_ROUTES[primaryRole] || "/student/dashboard", { replace: true });
        if (addToast) {
          addToast({
            message: "Super Administrator privileges required.",
            bg: "danger",
          });
        }
        return;
      }

      if (
        inStaffGroup &&
        !isSuperuser &&
        !userRoles.includes(ROLES.ADMIN) &&
        !userRoles.includes(ROLES.SUPERADMIN) &&
        !userData.is_staff
      ) {
        navigate(DASHBOARD_ROUTES[primaryRole] || "/student/dashboard", { replace: true });
        if (addToast) {
          addToast({
            message: "Administrative privileges required.",
            bg: "danger",
          });
        }
        return;
      }

      if (
        inInstructorGroup &&
        !isSuperuser &&
        !userRoles.includes(ROLES.INSTRUCTOR) &&
        !userRoles.includes(ROLES.SUPERADMIN)
      ) {
        navigate(DASHBOARD_ROUTES[primaryRole] || "/student/dashboard", { replace: true });
        if (addToast) {
          addToast({
            message: "Instructor privileges required.",
            bg: "danger",
          });
        }
        return;
      }

      // Authorized for current route
      setIsReady(true);
    }
  }, [
    isLoading,
    isAuthenticated,
    currentUser,
    location.pathname,
    location.search,
    navigate,
    addToast,
    allowedRoles,
  ]);

  if (!isReady) {
    return (
      <div className="cht-fixed cht-inset-0 cht-bg-[#0f172a] cht-z-[9999] cht-flex cht-flex-col cht-items-center cht-justify-center cht-space-y-4 cht-text-white cht-backdrop-blur-xl">
        <div className="cht-relative cht-flex cht-items-center cht-justify-center">
          <div className="cht-h-16 cht-w-16 cht-rounded-squircle cht-bg-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shadow-2xl cht-shadow-[#3e57da]/40">
            <GraduationCap className="cht-w-8 cht-h-8 cht-text-white" />
          </div>
          <Loader2 className="cht-w-20 cht-h-20 cht-text-[#3e57da] cht-animate-spin cht-absolute -cht-inset-2" />
        </div>
        <div className="cht-text-center cht-space-y-1">
          <p className="cht-text-xs cht-font-black cht-text-white cht-tracking-widest cht-uppercase cht-font-heading">
            Codehub Technologies
          </p>
          <p className="cht-text-[11px] cht-text-slate-400 cht-font-medium">
            Verifying session security...
          </p>
        </div>
      </div>
    );
  }

  return children ? <>{children}</> : <Outlet />;
}

export default AuthGuard;
